import React, { useState } from 'react';
import { Share2, ExternalLink, Check, MoreHorizontal, Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WatchButton } from '@/components/ui/watch-button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

type EntityRoute = { route: string; idParam?: string };

// Mirror of ENTITY_ROUTE_MAP in flyout.tsx. Keep in sync.
// (Plan note: future cleanup — promote to a single shared module.)
const ENTITY_ROUTE_MAP: Record<string, EntityRoute> = {
  task: { route: '/tasks' },
  intent: { route: '/intents', idParam: 'intentId' },
  reservation: { route: '/reservations' },
  review: { route: '/reviews' },
  property: { route: '/properties' },
  guest: { route: '/guests' },
  owner: { route: '/owners' },
  'staff-attendance': { route: '/staff-attendance' },
  // KB 有独立详情页（正文 + 详情侧栏）；列表页的 ?unit= flyout 深链仍兼容旧链接。
  knowledge_unit: { route: '/knowledge-base' },
  // procurement purchase detail lives in a flyout on the list page (query-param deep link)
  purchase: { route: '/procurement', idParam: 'purchaseId' },
  procurement_purchase: { route: '/procurement', idParam: 'purchaseId' },
  inventory_balance: { route: '/asset-mgr/inventory', idParam: 'balanceId' },
  product: { route: '/asset-mgr/products', idParam: 'productId' },
  inventory_transaction: { route: '/asset-mgr/transactions', idParam: 'transactionId' },
};

export function buildEntityUrl(
  entityType: string | undefined,
  entityId: string | undefined,
  urlParams?: Record<string, string>,
): string {
  if (!entityType || !entityId) return window.location.href;
  const mapping = ENTITY_ROUTE_MAP[entityType.toLowerCase()];
  if (!mapping) return window.location.href;
  const baseUrl = window.location.origin;
  if (mapping.idParam) {
    // IMPORTANT: idParam comes LAST in the spread so the entity id can never be
    // overridden by a caller-supplied urlParams entry with the same key.
    const params = new URLSearchParams({ ...(urlParams ?? {}), [mapping.idParam]: String(entityId) });
    return `${baseUrl}${mapping.route}?${params.toString()}`;
  }
  let url = `${baseUrl}${mapping.route}/${entityId}`;
  if (urlParams && Object.keys(urlParams).length > 0) {
    url += `?${new URLSearchParams(urlParams).toString()}`;
  }
  return url;
}

export interface ToolbarAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  loading?: boolean;
  destructive?: boolean;
  /**
   * 选中后收起菜单（打开 Dialog 的菜单项用）。默认 false：菜单保持打开，
   * 便于显示 loading（如 PDF 导出）。不收起的话 Dialog 弹出后菜单还挂在它背后。
   */
  closeOnSelect?: boolean;
}

export interface EntityToolbarButtonsProps {
  entityType?: string;
  entityId?: string;
  urlParams?: Record<string, string>;
  showWatch?: boolean;
  showShare?: boolean;
  showOpenNew?: boolean;
  readonly?: boolean;
  overflowActions?: ToolbarAction[];
  onRefresh?: () => void | Promise<void>;
}

export const EntityToolbarButtons: React.FC<EntityToolbarButtonsProps> = ({
  entityType,
  entityId,
  urlParams,
  showWatch = true,
  showShare = true,
  showOpenNew = true,
  readonly = false,
  overflowActions,
  onRefresh,
}) => {
  const { t } = useTranslation('common')
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    try { setRefreshing(true); await onRefresh(); } finally { setRefreshing(false); }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(buildEntityUrl(entityType, entityId, urlParams));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(buildEntityUrl(entityType, entityId, urlParams), '_blank');
  };

  const overflowItems: ToolbarAction[] = [
    ...(showOpenNew && entityType && entityId
      ? [{
          key: '__open_new_tab',
          label: t('ui.flyout.openInNewTab'),
          icon: <ExternalLink className="w-4 h-4" />,
          onSelect: handleOpenInNewTab,
        } as ToolbarAction]
      : []),
    ...(overflowActions ?? []),
  ];

  return (
    <>
      {!readonly && showWatch && entityType && entityId && (
        <>
          <WatchButton entityType={entityType} entityId={entityId} />
          <div className="h-5 w-px bg-border mx-1" />
        </>
      )}
      {onRefresh && (
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          title={t('ui.flyout.refresh')}
          className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      )}
      {showShare && (
        <div className="relative">
          <button
            onClick={handleShare}
            className={`p-2 rounded-md transition-colors ${
              copied ? 'text-green-600 bg-green-50 dark:bg-green-950/40' : 'text-muted-foreground hover:text-sky-600 hover:bg-accent'
            }`}
            title={t('ui.flyout.copyLink')}
            type="button"
          >
            {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
          </button>
          {copied && (
            <div className="absolute top-full right-0 mt-2 px-2 py-1 bg-foreground text-background text-[10px] font-bold rounded shadow-lg whitespace-nowrap z-50 animate-in fade-in zoom-in-95">
              {t('ui.flyout.linkCopied')}
            </div>
          )}
        </div>
      )}
      {overflowItems.length > 0 && (
        <DropdownMenu open={overflowOpen} onOpenChange={setOverflowOpen}>
          <DropdownMenuTrigger asChild>
            {/* 与 PrimaryActionBar 的 More 按钮 title 完全同名("More actions"),
                同一个抽屉里会同时出现两个。给两边各留一个稳定 testid,e2e 才能
                说清自己要点的是头部这个(Open in new tab / 导出 PDF)还是底部那个
                (Put On Hold / Cancel Task)。 */}
            <button
              type="button"
              data-testid="entity-toolbar-more-actions"
              title={t('ui.flyout.moreActions')}
              className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflowItems.map((a) => (
              <DropdownMenuItem
                key={a.key}
                disabled={a.disabled || a.loading}
                onSelect={(e) => {
                  // 仓库约定：菜单项打开弹层先 preventDefault（免得与弹层抢焦点），需要收起的手动收。
                  e.preventDefault();
                  if (a.closeOnSelect) setOverflowOpen(false);
                  a.onSelect();
                }}
                className={a.destructive ? 'text-red-600 focus:text-red-600' : undefined}
              >
                <span className="mr-2 inline-flex">
                  {a.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : a.icon}
                </span>
                {a.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
};
